/**
 * Column budget for the response outline's sections table (ArtifactDetailPage).
 *
 * The table is `table-fixed`: every column except Section has a set width and
 * Section takes the rest. Widths are listed in px next to their Tailwind class
 * so the arithmetic can be checked (sections-table-columns.test.ts) — the class
 * strings must stay literal for Tailwind's scanner.
 *
 * Budget (below 2xl, Updated hidden):
 *   drag 24 + # 28 + status 172 + owner 120 + reviewer 120 + submission 96
 *   + words 116 + brief 80 + actions 40 = 796px fixed
 *   + Section minimum 226px = 1022px table minimum.
 * A 1280px window with the sidebar expanded gives a ~1038px table box, so the
 * table fits with no sideways scroll and nothing sits under the pinned actions
 * column; Section gets ~242px there. At 1440 (~1200px box) Section gets ~404px.
 *
 * Status is 172 so the longest pill, "Waiting on your review" (shown while an
 * AI suggestion waits on the section), fits on one line; Section's minimum
 * gave up the 22px so the 1280 fit holds.
 *
 * Brief has its own always-visible column; the pinned actions cell holds only
 * ⋯ (32px button + 8px padding). Owner/Reviewer came down to 120 because their
 * empty state is a 28px icon. Words is 116 so its left padding can open a clear
 * gap from Submission while "661 / 650" and the 28px bar still share one line.
 *
 * From 2xl (>=1536px viewport, box >=1296px) the Updated column (72px) returns:
 * 868px fixed, Section gets the rest (~430px+).
 */

export interface SectionsTableColumn {
  key:
    | 'drag'
    | 'number'
    | 'section'
    | 'status'
    | 'owner'
    | 'reviewer'
    | 'submission'
    | 'words'
    | 'brief'
    | 'updated'
    | 'actions'
  /** Fixed width in px; null for Section, which takes the remaining width. */
  widthPx: number | null
  /** Classes for the `<col>`, width included. */
  colClassName: string
  /** Only shown from the 2xl breakpoint up. */
  wideOnly?: boolean
}

export const SECTIONS_TABLE_COLUMNS: readonly SectionsTableColumn[] = [
  { key: 'drag', widthPx: 24, colClassName: 'w-6' },
  { key: 'number', widthPx: 28, colClassName: 'w-7' },
  { key: 'section', widthPx: null, colClassName: '' },
  { key: 'status', widthPx: 172, colClassName: 'w-[172px]' },
  { key: 'owner', widthPx: 120, colClassName: 'w-[120px]' },
  { key: 'reviewer', widthPx: 120, colClassName: 'w-[120px]' },
  { key: 'submission', widthPx: 96, colClassName: 'w-[96px]' },
  { key: 'words', widthPx: 116, colClassName: 'w-[116px]' },
  { key: 'brief', widthPx: 80, colClassName: 'w-20' },
  // `display: none` on the <col> removes the column, so the pinned actions
  // cell keeps its own 40px rather than inheriting Updated's slot.
  { key: 'updated', widthPx: 72, colClassName: 'hidden 2xl:table-column w-[72px]', wideOnly: true },
  { key: 'actions', widthPx: 40, colClassName: 'w-10' },
]

/** Section never gets narrower than this; below it the table scrolls sideways. */
export const SECTION_MIN_WIDTH_PX = 226

/** Must match the `min-w-[…]` class on the table. */
export const SECTIONS_TABLE_MIN_WIDTH_PX = 1022

/** Header/cell classes for the Updated column: hidden below 2xl. */
export const UPDATED_CELL_VISIBILITY = 'hidden 2xl:table-cell'
